import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import { useNavigate } from "react-router";
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import EnterpriseService from "../../common/services/enterprise-service";
import truckyService from "../../common/services/trucky-service";
import CustomBadge from "../ui/CustomBadge";
import ProvisioningStatusBadge from "./ProvisioningStatusBadge";
import ServerStatusBadge from "./ServerStatusBadge";
import { EnterpriseContext } from "../enterprise/EnterpriseContext";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import {
  HOSTING_2GB_PLAN,
  LEGACY_HOSTING_PLAN,
  SERVER_GENERATION_NAMES,
} from "../../common/constants/hosting";

const DedicatedServerHostingList = ({ container }) => {
  const [servers, setServers] = useState([]);
  const navigate = useNavigate();
  const es = new EnterpriseService();
  const [subscriptionsChecked, setSubscriptionsChecked] = useState(false);
  const [dedicatedServerSubscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const { globalContext } = useContext(EnterpriseContext);
  const [myServersCount, setMyServersCount] = useState(0);
  const [requiresHostingUpgrade, setRequiresHostingUpgrade] = useState(false);
  const [hasActiveHosting2GbSubscription, setHasActiveHosting2GbSubscription] =
    useState(false);

  useEffect(() => {
    truckyService.setDocumentTitle("Dedicated Servers");

    getData();
  }, []);

  useEffect(() => {
    if (subscriptionsChecked && dedicatedServerSubscription == null) {
    }
  }, [subscriptionsChecked, dedicatedServerSubscription]);

  const getData = async () => {
    setLoading(true);
    setLoadingSubscriptions(true);

    const servers = await es.getUserDedicatedServers();

    setServers(servers);

    const userSubscriptions = await es.getUserSubscriptions();
    const hasLegacyHostingSubscription = userSubscriptions.subscriptions.some(
      (subscription) =>
        subscription.plan == LEGACY_HOSTING_PLAN &&
        subscription.state != "deactivated",
    );
    const hasActiveHosting2GbSubscription =
      userSubscriptions.subscriptions.some(
        (subscription) =>
          subscription.plan == HOSTING_2GB_PLAN &&
          subscription.state == "active",
      );

    setHasActiveHosting2GbSubscription(hasActiveHosting2GbSubscription);
    setRequiresHostingUpgrade(
      hasLegacyHostingSubscription && !hasActiveHosting2GbSubscription,
    );

    if (userSubscriptions.subscriptions.length > 0) {
      const dedicatedServerSubscription = userSubscriptions.subscriptions.find(
        (m) =>
          m.plan == LEGACY_HOSTING_PLAN ||
          m.plan == "dedicated-server-hosting-yearly" ||
          m.plan == HOSTING_2GB_PLAN,
      );

      if (dedicatedServerSubscription) {
        setSubscription(dedicatedServerSubscription);
      }
    }

    setLoading(false);
    setLoadingSubscriptions(false);

    if (userSubscriptions.subscriptions.length == 0 && servers.length == 0)
      navigate("/my/subscriptions");
  };

  useEffect(() => {
    setMyServersCount(
      servers.filter((m) => m.user_id == globalContext.member.id).length,
    );
  }, [servers]);

  const getProvisioningStatus = (status) => {
    let color = "danger";

    switch (status) {
      case "not_provisioned":
        color = "danger";
        break;
      case "provisioned":
        color = "success";
        break;
      case "provisioning":
        color = "warning";
        break;
      case "creating":
        color = "warning";
        break;
    }

    return (
      <CustomBadge color={color}>
        {container.localeManager.strings[status]}
      </CustomBadge>
    );
  };

  return (
    <>
      <LoadingOverlay
        active={loading}
        spinner={
          <Spinner
            color="primary"
            style={{ width: "4rem", height: "4rem" }}
          ></Spinner>
        }
      >
        <Row className="gx-0">
          <Col>
            <Card>
              <CardBody>
                <div class="mb-3 d-flex justify-content-between">
                  {window.overwolf && (
                    <Button
                      size="sm"
                      onClick={() => navigate(-1)}
                      color="secondary"
                    >
                      <FontAwesomeIcon icon="chevron-left"></FontAwesomeIcon>
                      {container.localeManager.strings.back}
                    </Button>
                  )}
                  {!loadingSubscriptions &&
                    dedicatedServerSubscription != null &&
                    servers.length > 0 && (
                      <div>
                        <Button
                          disabled={
                            dedicatedServerSubscription.quantity ==
                            myServersCount
                          }
                          color="primary"
                          size="sm"
                          onClick={() => navigate("/hosting/server")}
                        >
                          <FontAwesomeIcon icon="plus"></FontAwesomeIcon>
                          {container.localeManager.strings.createANewServer} (
                          {container.localeManager.strings.quota}{" "}
                          {myServersCount}/
                          {dedicatedServerSubscription.quantity})
                        </Button>
                      </div>
                    )}
                </div>
                <h5 className="d-inline-block w-100 mb-5">
                  {container.localeManager.strings.dedicatedServers}
                </h5>
                {requiresHostingUpgrade && !loadingSubscriptions && (
                  <div className="notice bg-light-warning rounded border-warning border border-dashed p-6 mb-5 d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <div>
                      <div className="fw-bold mb-1">
                        <strong>Attention:</strong> Subscription Upgrade
                        Required
                      </div>
                      <div>
                        ETS2 and ATS Update 1.61 requires additional hardware
                        resources. Upgrade your Gen 1 Server subscription to
                        Gen 2 Server to continue using the service.
                        <br />
                        Otherwise you can continue using Gen 1 Server, but
                        the server won't work for 1.61. You can continue to use
                        it for previous versions of the game.
                      </div>
                    </div>
                    <Button
                      color="warning"
                      size="sm"
                      onClick={() => navigate("/my/subscriptions")}
                    >
                      <FontAwesomeIcon icon="arrow-up" />
                      {container.localeManager.strings.upgrade || "Upgrade"}
                    </Button>
                  </div>
                )}
                {dedicatedServerSubscription != null &&
                  !loadingSubscriptions &&
                  servers.length == 0 && (
                    <div className="text-center my-5">
                      <Button
                        color="primary"
                        size="lg"
                        onClick={() => navigate("/hosting/server")}
                      >
                        <FontAwesomeIcon icon="plus"></FontAwesomeIcon>
                        {container.localeManager.strings.createYourFirstServer}
                      </Button>
                    </div>
                  )}
                {servers.length > 0 && (
                  <div className="grid cols-2">
                    {servers != null &&
                      servers.map((m) => {
                        return (
                          <Card className="mb-3 bg-light border border-hover border-primary">
                            <CardBody>
                              <div className="d-flex align-items-start justify-content-between mb-3">
                                <div>
                                  <div className="fs-4 fw-bold">
                                    {m.lobby_name}
                                  </div>
                                  <div className="text-muted fs-7 mt-1">
                                    <FontAwesomeIcon
                                      icon="gamepad"
                                      className="me-1"
                                    />
                                    {m.game.code}
                                    {m.region_id && (
                                      <>
                                        <span className="mx-2">·</span>
                                        <FontAwesomeIcon
                                          icon="location-dot"
                                          className="me-1"
                                        />
                                        {m.region_id}
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex flex-column align-items-end gap-1">
                                  {SERVER_GENERATION_NAMES[m.size_id] && (
                                    <CustomBadge color="primary">
                                      <FontAwesomeIcon
                                        icon="microchip"
                                        className="me-1"
                                      />
                                      {SERVER_GENERATION_NAMES[m.size_id]}
                                    </CustomBadge>
                                  )}
                                  {hasActiveHosting2GbSubscription &&
                                    m.user_id == globalContext.member.id &&
                                    m.size_id == "s-1vcpu-1gb" &&
                                    m.provisioning_status == "provisioned" && (
                                      <CustomBadge color="warning">
                                        <FontAwesomeIcon
                                          icon="arrow-up"
                                          className="me-1"
                                        />
                                        Upgrade available
                                      </CustomBadge>
                                    )}
                                  <ProvisioningStatusBadge
                                    container={container}
                                    status={m.provisioning_status}
                                  ></ProvisioningStatusBadge>
                                  {m.provisioning_status == "provisioned" && (
                                    <ServerStatusBadge
                                      container={container}
                                      server={m}
                                    ></ServerStatusBadge>
                                  )}
                                </div>
                              </div>
                              {m.droplet_ip_address != null && (
                                <div className="mb-3">
                                  <span className="badge badge-success px-2 py-1">
                                    <FontAwesomeIcon
                                      icon="server"
                                      className="me-2"
                                    />
                                    {m.droplet_ip_address}
                                  </span>
                                </div>
                              )}
                              <div className="d-flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/hosting/server/${m.id}`)
                                  }
                                  color="primary"
                                >
                                  <FontAwesomeIcon
                                    icon="pencil"
                                    className="me-1"
                                  />
                                  Configure
                                </Button>
                                {m.server_status_url != null && (
                                  <Button
                                    size="sm"
                                    href={m.server_status_url}
                                    tag="a"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="info"
                                  >
                                    <FontAwesomeIcon
                                      icon="circle-info"
                                      className="me-1"
                                    />
                                    Status API
                                  </Button>
                                )}
                                {m.server_logs_url != null && (
                                  <Button
                                    size="sm"
                                    href={m.server_logs_viewer_url}
                                    tag="a"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="warning"
                                  >
                                    <FontAwesomeIcon
                                      icon="file-lines"
                                      className="me-1"
                                    />
                                    Logs
                                  </Button>
                                )}
                              </div>
                            </CardBody>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <aside className="ad-column-placeholder">
            {!window.overwolf && (
              <BoxDynamicAd container={container} className="my-3" />
            )}
          </aside>
        </Row>
      </LoadingOverlay>
    </>
  );
};

export default DedicatedServerHostingList;
